import { Autocomplete, TextField } from "@mui/material";
import { controlBandChipFieldSx } from "../../theme/compact.js";
import type { ReactElement } from "react";

/**
 * `<MultiSelectChipFilter>` — canonical multi-select pill filter.
 *
 * Spec: `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 5 /
 * MultiSelectChipFilter".
 *
 * - `<Autocomplete multiple disableCloseOnSelect>`.
 * - 280 px wide, `size="small"`.
 * - One `<Chip>` per picked value with × to remove (MUI default).
 * - Hover-× on the whole field clears all picks (MUI default).
 * - Inner `<input>` carries `role="combobox"` with accessible name =
 *   the field label so `getByRole("combobox", { name: /^manufacturer$/i })`
 *   matches.
 * - For enum-typed filters, pass `optionLabel` so the chip reads
 *   "Rotor blade" while the URL keeps `ROTOR_BLADE`.
 */
export interface MultiSelectChipFilterProps {
  label: string;
  options: readonly string[];
  value: readonly string[];
  onChange: (next: readonly string[]) => void;
  /** Map a raw option to its visible label. Defaults to identity. */
  optionLabel?: (option: string) => string;
  /** Per-page width override. Defaults to 280 px (spec). */
  width?: number;
  /**
   * Copy shown in the dropdown body when `options.length === 0`. Maps
   * to MUI Autocomplete's `noOptionsText`. Added 2026-05-20 / SB2-W-04
   * so the Parks & Sites country filter can render
   * "No countries yet" instead of MUI's default "No options" copy when
   * the active tenant has zero sites.
   */
  noOptionsText?: string;
  /**
   * Optional per-option matcher for typed input. When omitted, MUI
   * Autocomplete's default substring match against
   * `getOptionLabel(option)` applies (case-insensitive).
   *
   * Added 2026-05-20 / SB2-W-09 so the Sites country filter can match
   * against the option's alpha-2 + alpha-3 + English name even though
   * only the alpha-2 + English name appear in the visible chip label.
   * The callback receives the raw option (e.g. an alpha-2 country
   * code) and the raw typed input, and returns true when the option
   * should remain in the dropdown.
   */
  matchesQuery?: (option: string, query: string) => boolean;
}

export function MultiSelectChipFilter({
  label,
  options,
  value,
  onChange,
  optionLabel,
  width = 280,
  noOptionsText,
  matchesQuery,
}: MultiSelectChipFilterProps): ReactElement {
  const getLabel = optionLabel ?? ((o: string) => o);
  // SB2-W-09 — when the consumer supplies `matchesQuery`, override MUI
  // Autocomplete's default substring filter so the typed input can be
  // matched against fields that are NOT in the visible label (e.g.
  // alpha-3 country codes for a chip that only renders alpha-2 +
  // English name). When `matchesQuery` is omitted, the prop is
  // spread-omitted below so MUI's default filter applies.
  const filterOptions =
    matchesQuery !== undefined
      ? (opts: string[], state: { inputValue: string }) => {
          const q = state.inputValue;
          if (!q || q.trim().length === 0) return opts;
          return opts.filter((o) => matchesQuery(o, q));
        }
      : undefined;
  return (
    <Autocomplete<string, true>
      multiple
      disableCloseOnSelect
      size="small"
      options={[...options]}
      value={[...value]}
      isOptionEqualToValue={(a, b) => a === b}
      getOptionLabel={getLabel}
      onChange={(_e, next) => {
        onChange(next);
      }}
      sx={{
        // `denseChipFieldSx`, not `denseFieldSx`: this control has to
        // GROW past the band when several chips are selected, so the
        // band is a minimum here rather than a height.
        ...controlBandChipFieldSx,
        width,
        flexShrink: 0,
        // SB6-II review fix — cap each chip's width so it never fills the whole
        // field; this reserves room for the inner <input> on the SAME row, so a
        // single picked chip no longer pushes the (empty) input onto a phantom
        // second line. Chips still wrap to real rows as more are picked.
        "& .MuiAutocomplete-tag": { maxWidth: "calc(100% - 44px)" },
      }}
      {...(filterOptions !== undefined ? { filterOptions } : {})}
      {...(noOptionsText !== undefined ? { noOptionsText } : {})}
      renderInput={(p) => {
        // MUI v6's Autocomplete hands back the legacy
        // `InputProps`/`inputProps`/`InputLabelProps` triplet — translate
        // to the modern `slotProps` shape so the TextField stays current.
        // SB6-CONS-01 — `minWidth: 0` on the inner <input> overrides
        // MUI's built-in `min-width: 30px` so the input collapses next
        // to the chips when they fit on one row. Without this the
        // input forces a wrap to a second line the moment the chip
        // strip + the 30 px input no longer share a row — the empty-
        // row-below-chips bug Christian called out on the
        // Contracts / Contract-Positions / Users / Audit-Log filters.
        const { InputLabelProps, InputProps, inputProps, ...rest } = p;
        const tfProps = {
          ...rest,
          label,
          slotProps: {
            htmlInput: { ...inputProps, style: { minWidth: "0px" } },
            input: InputProps,
            inputLabel: { ...InputLabelProps, shrink: true },
          },
        } as unknown as React.ComponentProps<typeof TextField>;
        return <TextField {...tfProps} />;
      }}
    />
  );
}
